import { useEffect, useRef, useState } from "react";
import {
  NoteDTO,
  NOTE_SIZE,
  clampNoteWidth,
  clampNoteHeight,
} from "@retrospecter/shared/types";
import { apiPatch, apiPost, apiDelete } from "../../api/client";
import { ColumnAccent } from "./columnAccents";
import { NoteReactions } from "./NoteReactions";
import { NUDGE_STEP, Placement } from "./noteLayout";
import { NoteDragStart } from "../../hooks/useNoteDrag";
import { useTranslation } from "../../i18n";

export type MoveDirection = "up" | "down" | "left" | "right";

interface NoteCardProps {
  note: NoteDTO;
  accent: ColumnAccent;
  /**
   * Where the post-it sits on its column's canvas, already fitted to the
   * canvas's width. Absent for a note rendered inside its group's head card,
   * which flows with the group rather than being placed.
   */
  placement?: Placement;
  /** Stacking order among the notes on the canvas. */
  zIndex?: number;
  /** Whether this viewer may edit or delete this particular note. */
  canManage: boolean;
  /**
   * Whether this viewer manages notes they did not write — the facilitator on
   * a live board. Used to decide the same for a group's members, which this
   * card renders itself.
   */
  canManageOthers?: boolean;
  /** Whether this viewer may vote and react (a participant on a live board). */
  canVote: boolean;
  /** Whether this viewer may move and resize notes. */
  canMove: boolean;
  isDragging: boolean;
  /**
   * Viewport position of the note while it is being dragged. The note follows
   * the pointer for real rather than leaving a ghost behind, so that where it
   * looks like it will land is where it lands.
   */
  floatingAt?: { left: number; top: number } | null;
  onDragStart: (start: Omit<NoteDragStart, "noteId" | "columnId">) => void;
  /** Commits a new size once the resize gesture finishes. */
  onResize: (size: { width: number; height: number }) => void;
  /** Keyboard equivalent of dragging: nudge, or shift to change column. */
  onKeyboardMove: (direction: MoveDirection, toColumn: boolean) => void;
  /** True while a dragged note is hovering over this card to join its group. */
  isGroupTarget?: boolean;
  /**
   * Keyboard equivalent of dropping this note onto another. Absent when there
   * is nothing to group it with.
   */
  onGroupWithPrevious?: () => void;
  /** Rendered inside its group's head card, so it drops the drag affordances. */
  isMember?: boolean;
  onChanged: () => void;
}

export function NoteCard({
  note,
  accent,
  placement,
  zIndex = 0,
  canManage,
  canManageOthers = false,
  canVote,
  canMove,
  isDragging,
  floatingAt,
  onDragStart,
  onResize,
  onKeyboardMove,
  isGroupTarget = false,
  onGroupWithPrevious,
  isMember = false,
  onChanged,
}: NoteCardProps) {
  const { t, plural } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.text);
  const [busy, setBusy] = useState(false);
  const [voting, setVoting] = useState(false);
  const [error, setError] = useState("");

  const cardRef = useRef<HTMLElement>(null);

  /** Live size while the corner is being dragged, so the note resizes under it. */
  const [resizing, setResizing] = useState<{ width: number; height: number } | null>(
    null
  );
  const resizeFrom = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const resizeTo = useRef<{ width: number; height: number } | null>(null);

  useEffect(() => {
    if (!resizing) return;

    const move = (event: PointerEvent) => {
      const from = resizeFrom.current;
      const next = {
        width: clampNoteWidth(from.width + event.clientX - from.x),
        height: clampNoteHeight(from.height + event.clientY - from.y),
      };
      resizeTo.current = next;
      setResizing(next);
    };

    const finish = () => {
      const size = resizeTo.current;
      setResizing(null);
      resizeTo.current = null;
      if (size) onResize(size);
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", finish);

    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", finish);
    };
    // Re-subscribing on every pixel is pointless: only the transition into and
    // out of a resize matters.
  }, [resizing !== null, onResize]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleVote = async () => {
    setVoting(true);
    setError("");
    try {
      if (note.votedByMe) {
        await apiDelete(`/api/notes/${note.id}/vote`);
      } else {
        await apiPost(`/api/notes/${note.id}/vote`, {});
      }
      onChanged();
    } catch {
      setError(t("note.voteError"));
    } finally {
      setVoting(false);
    }
  };

  const save = async () => {
    const text = draft.trim();
    if (!text || text === note.text) {
      setEditing(false);
      setDraft(note.text);
      return;
    }

    setBusy(true);
    setError("");
    try {
      await apiPatch(`/api/notes/${note.id}`, { text });
      setEditing(false);
      onChanged();
    } catch {
      setError(t("note.saveError"));
    } finally {
      setBusy(false);
    }
  };

  /** Grouping and ungrouping are the same call with a different parent. */
  const setGroup = async (parentId: string | null) => {
    setBusy(true);
    setError("");
    try {
      await apiPatch(`/api/notes/${note.id}/group`, { parentId });
      onChanged();
    } catch {
      setError(t(parentId === null ? "note.ungroupError" : "note.groupError"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError("");
    try {
      await apiDelete(`/api/notes/${note.id}`);
      onChanged();
    } catch {
      setError(t("note.deleteError"));
      setBusy(false);
    }
  };

  const handleHandleKeyDown = (event: React.KeyboardEvent) => {
    const directions: Record<string, MoveDirection> = {
      ArrowUp: "up",
      ArrowDown: "down",
      ArrowLeft: "left",
      ArrowRight: "right",
    };

    const direction = directions[event.key];
    if (!direction) return;

    event.preventDefault();
    // Arrows nudge the note around its canvas; with Shift they hand it to the
    // next column, which is otherwise a mouse-only journey.
    onKeyboardMove(direction, event.shiftKey);
  };

  /**
   * Anything interactive inside the note has to stay clickable, so a press
   * that lands on a control is not the start of a drag.
   */
  const startDrag = (event: React.PointerEvent) => {
    if (!canMove || isMember || editing) return;
    if (event.button !== 0) return;
    if ((event.target as HTMLElement).closest("button, textarea, input, a")) return;

    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;

    onDragStart({
      rect: {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      },
      clientX: event.clientX,
      clientY: event.clientY,
    });
  };

  const startResize = (event: React.PointerEvent) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();

    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;

    resizeFrom.current = {
      x: event.clientX,
      y: event.clientY,
      width: rect.width,
      height: rect.height,
    };
    const size = { width: rect.width, height: rect.height };
    resizeTo.current = size;
    setResizing(size);
  };

  const resizeByKey = (event: React.KeyboardEvent) => {
    const steps: Record<string, { width: number; height: number }> = {
      ArrowRight: { width: NUDGE_STEP, height: 0 },
      ArrowLeft: { width: -NUDGE_STEP, height: 0 },
      ArrowDown: { width: 0, height: NUDGE_STEP },
      ArrowUp: { width: 0, height: -NUDGE_STEP },
    };

    const step = steps[event.key];
    if (!step) return;

    event.preventDefault();
    onResize({
      width: clampNoteWidth((placement?.width ?? NOTE_SIZE.defaultWidth) + step.width),
      height: clampNoteHeight(
        (placement?.height ?? NOTE_SIZE.defaultHeight) + step.height
      ),
    });
  };

  const size = resizing ?? placement;

  // A member flows inside its group's card; every other note is placed on the
  // canvas, and while it is being dragged it is pinned to the pointer instead.
  const style: React.CSSProperties = {
    ["--lane" as string]: accent.lane,
    ...(isMember
      ? {}
      : isDragging && floatingAt
      ? {
          position: "fixed",
          left: floatingAt.left,
          top: floatingAt.top,
          width: size?.width,
          height: size?.height,
          zIndex: 60,
        }
      : placement
      ? {
          position: "absolute",
          left: placement.x,
          top: placement.y,
          width: size?.width,
          height: size?.height,
          zIndex,
        }
      : {}),
    ...(canMove && !isMember ? { touchAction: "none" } : {}),
  };

  return (
    <article
      ref={cardRef}
      data-note-root={isMember ? undefined : note.id}
      data-testid="note-card"
      style={style}
      onPointerDown={startDrag}
      aria-roledescription={canMove && !isMember ? t("note.draggable") : undefined}
      className={`post-it group flex flex-col overflow-hidden rounded-xl border shadow-subtle ${
        isMember ? "relative" : ""
      } ${
        isDragging
          ? "post-it-carried pointer-events-none shadow-float"
          : "transition-shadow duration-200 hover:shadow-raised"
      } ${canMove && !isMember && !editing ? "cursor-grab active:cursor-grabbing" : ""} ${
        isGroupTarget ? "ring-2 ring-nm-accent ring-offset-2 ring-offset-canvas" : ""
      }`}
    >
      {editing ? (
        <div className="flex flex-1 flex-col gap-2 overflow-auto p-2.5">
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                save();
              }
              if (event.key === "Escape") {
                setEditing(false);
                setDraft(note.text);
              }
            }}
            className="input-field min-h-[3rem] flex-1 resize-none px-2.5 py-2 text-sm"
            autoFocus
            aria-label={t("note.editNote")}
          />
          <div className="flex gap-2">
            <button
              onClick={save}
              disabled={busy || !draft.trim()}
              className="btn-accent flex-1 px-3 py-1.5 text-sm"
            >
              {busy ? t("common.saving") : t("common.save")}
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setDraft(note.text);
                setError("");
              }}
              className="btn-ghost px-3 py-1.5 text-sm"
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* The note's own words scroll inside the paper: a post-it people
              resized to a size they chose must not grow back on its own. */}
          <div className="flex-1 overflow-auto px-3 pb-1 pt-3">
            <p className="whitespace-pre-wrap break-words text-sm font-medium leading-relaxed text-ink">
              {note.text}
            </p>

            {note.children.length > 0 && (
              <div className="mt-3 border-t border-dashed border-line pt-2.5">
                <p className="mb-2 flex flex-wrap items-center justify-between gap-2 text-[0.68rem] font-semibold uppercase tracking-[0.06em] text-ink-faint">
                  <span>{plural("note.groupedNotes", note.children.length + 1)}</span>
                  <span className="normal-case tracking-normal">
                    {t("note.groupSummary", { votes: note.totalVotes })}
                  </span>
                </p>

                <ul className="space-y-2">
                  {note.children.map((child) => (
                    <li key={child.id}>
                      <NoteCard
                        note={child}
                        accent={accent}
                        // A member is managed by whoever wrote it, or by the
                        // facilitator — not by whoever happens to own the head.
                        canManage={canManageOthers || (child.isMine && canVote)}
                        canManageOthers={canManageOthers}
                        canVote={canVote}
                        canMove={canMove}
                        isDragging={false}
                        onDragStart={() => {}}
                        onResize={() => {}}
                        onKeyboardMove={() => {}}
                        isMember
                        onChanged={onChanged}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="px-3 pb-2.5">
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <button
                  onClick={toggleVote}
                  disabled={!canVote || voting}
                  aria-pressed={note.votedByMe}
                  aria-label={note.votedByMe ? t("note.unvote") : t("note.vote")}
                  title={canVote ? undefined : t("note.voteHint")}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold transition-all duration-150 ${
                    note.votedByMe
                      ? "bg-nm-accent text-white shadow-subtle"
                      : "bg-surface/70 text-ink-muted"
                  } ${
                    canVote
                      ? "cursor-pointer hover:scale-105 hover:bg-nm-accent hover:text-white"
                      : "cursor-default opacity-60"
                  }`}
                >
                  <span aria-hidden="true">▲</span>
                  <span className="tabular-nums">{note.votes}</span>
                </button>

                {/* Only your own notes are marked, so you can find them again.
                    Everyone else's stay unattributed. */}
                {note.isMine && (
                  <span className="truncate text-xs text-ink-faint">
                    {t("note.yourNote")}
                  </span>
                )}
              </div>
            </div>

            <NoteReactions note={note} canReact={canVote} onChanged={onChanged} />
          </div>
        </>
      )}

      {!editing && (
        <div className="absolute right-1.5 top-1.5 flex items-center gap-0.5 rounded-lg bg-surface/90 px-0.5 opacity-0 shadow-subtle backdrop-blur-sm transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          {canManage && (
            <>
              <button
                onClick={() => setEditing(true)}
                disabled={busy}
                aria-label={t("note.editNote")}
                className="rounded-md px-1.5 py-1 text-xs text-ink-faint transition-colors hover:bg-surface-sunken hover:text-nm-primary"
              >
                {t("note.edit")}
              </button>
              <button
                onClick={remove}
                disabled={busy}
                aria-label={t("note.deleteNote")}
                className="rounded-md px-1.5 py-1 text-xs text-ink-faint transition-colors hover:bg-red-500/10 hover:text-red-500"
              >
                {busy ? "…" : t("note.delete")}
              </button>
            </>
          )}

          {/* Keyboard equivalent of resting one card on another. Grouping is
              otherwise pointer-only, and it is how duplicates get merged. */}
          {canMove && !isMember && onGroupWithPrevious && (
            <button
              onClick={onGroupWithPrevious}
              disabled={busy}
              aria-label={t("note.groupWithPrevious")}
              data-testid="group-note"
              className="rounded-md px-1.5 py-1 text-xs text-ink-faint transition-colors hover:bg-surface-sunken hover:text-nm-accent"
            >
              <span aria-hidden="true">⧉</span>
            </button>
          )}

          {canMove && isMember && (
            <button
              onClick={() => setGroup(null)}
              disabled={busy}
              aria-label={t("note.ungroup")}
              data-testid="ungroup-note"
              className="rounded-md px-1.5 py-1 text-xs text-ink-faint transition-colors hover:bg-surface-sunken hover:text-nm-accent"
            >
              <span aria-hidden="true">⤴</span>
            </button>
          )}

          {canMove && !isMember && (
            <button
              onKeyDown={handleHandleKeyDown}
              aria-label={t("note.move")}
              data-testid="move-note"
              title={t("note.moveHint")}
              className="cursor-grab rounded-md px-1.5 py-1 text-sm leading-none text-ink-faint transition-colors hover:bg-surface-sunken hover:text-ink-soft active:cursor-grabbing"
            >
              <span aria-hidden="true">⠿</span>
            </button>
          )}
        </div>
      )}

      {/* The corner you pull to resize. It is a real button so the same thing
          can be done with the arrow keys — a note that only a mouse can resize
          is a note some people can never read in full. */}
      {canMove && !isMember && !editing && (
        <button
          onPointerDown={startResize}
          onKeyDown={resizeByKey}
          aria-label={t("note.resize")}
          title={t("note.resizeHint")}
          data-testid="resize-note"
          style={{ touchAction: "none" }}
          className="absolute bottom-0 right-0 h-5 w-5 cursor-nwse-resize rounded-br-xl text-ink-faint opacity-0 transition-opacity after:absolute after:bottom-1 after:right-1 after:h-2 after:w-2 after:border-b-2 after:border-r-2 after:border-current after:content-[''] focus-visible:opacity-100 group-hover:opacity-100"
        />
      )}

      {error && (
        <p className="px-3 pb-2 text-xs text-red-500" role="status">
          {error}
        </p>
      )}
    </article>
  );
}
